/* eslint-disable react-hooks/rules-of-hooks */
import Delegate, { accessOptions } from 'organisms/Delegate';
import { useEffect, useState } from 'react';
import { createDelegate, getDelegates, removeDelegate, checkDelegationIsExist } from 'services/delegate.service';
import { getAllResources } from 'services/rolesAndPermissions.service';
import useGlobalToastStore from 'store/globalToastStore';
import { useSession } from 'next-auth/react';
import { DELEGATION_SUB_TYPES } from '@/helper/constants';

const getRefactoredResource = (resource) => {
    const {
        employeeCode, name, designation, subServiceArea, serviceArea, employeeFunction, user
    } = resource;
    return {
        id: user.id,
        userpic: '/assets/images/avatar.svg',
        employeeCode,
        name,
        designation,
        subServiceArea,
        serviceArea,
        employeeFunction,
        delegationAccess: resource?.delegation
    };
};

const mapDelegatedResources = (resources, delegateActivityId) => {
    if (resources.length) {
        const resourceMap = {};

        resources.forEach(resource => {
            const userId = resource.user.id;
            const delegationType = resource?.user?.delegate?.type;

            if (!resourceMap[userId]) {
                resourceMap[userId] = {
                    userId: userId,
                    delegateTypes: [],
                    resources: []
                };
            }

            if (delegationType && !resourceMap[userId]?.delegateTypes?.includes(delegationType)) {
                resourceMap[userId]?.delegateTypes?.push({ ...resource?.user?.delegate });
            }

            resourceMap[userId].resources.push(resource);
        });

        return Object.values(resourceMap).map(res => {
            if (res?.resources?.length) {
                const filtersItem = res.resources.filter((delegate) => delegate?.user?.delegate?.activityId === delegateActivityId)
                if (filtersItem?.length) { return { ...filtersItem[0], delegation: res?.delegateTypes } }
                return { ...res.resources[0], delegation: res?.delegateTypes }
            }

            return {}
        });

    }

    return [];
}

const resourceDataMaker = (resources) => resources.map(getRefactoredResource);

export const delegatesDataMaker = (delegates) => delegates.map((delegateData) => {
    const { delegate, type, delegateId } = delegateData;
    const delegateSessionType = ['specificProject', 'specificRequisition']
    const activityCode = delegateData?.specificProject?.spineCode || delegateData?.specificRequisition?.requisitionCode;

    return {
        ...getRefactoredResource({ ...delegate.resource, user: { id: delegateId } }),
        access: accessOptions.find((access) => access.value === type),
        delegatePrimaryId: delegates.map((delegate) => delegateSessionType.includes(type) && delegate?.id),
        delegation: delegateData,
        isDelegate: delegates.length ? true : false,
        spineCodes: delegateSessionType.includes(type) ? [activityCode] : []
    };
});

function DelegateActivity({ onClose, show }) {
    if (!show) return null;
    const session = useSession();
    const [resources, setResources] = useState([]);
    const [delegatedActivities, setDelegatedActivities] = useState([]);
    const [searchValue, setSearchValue] = useState('')
    const { setToastData } = useGlobalToastStore();
    const fetchResources = async (search) => {
        const res = await getAllResources({
            search, offset: 0, limit: 10, excludeSelf: true, isDelegate: true, loginOnBehalf: true
        });
        if (res.status) {
            const mappedDelegatedResources = mapDelegatedResources(res.entity.rows);
            const formatedDelegatesRows = resourceDataMaker(mappedDelegatedResources);
            setResources({ rows: formatedDelegatesRows, count: formatedDelegatesRows.length });
        }
    };

    const updateResources = async () => {
        const res = await getAllResources({
            search: searchValue, offset: resources?.rows?.length ?? 0, limit: 10, excludeSelf: true, isDelegate: true, loginOnBehalf: true
        });
        if (res.status) {
            const mappedDelegatedResources = mapDelegatedResources(res.entity.rows);
            const formatedDelegatesRows = resourceDataMaker(mappedDelegatedResources);
            setResources({ rows: formatedDelegatesRows, count: formatedDelegatesRows.length });
        }
    };

    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
        };
    };

    const onSearch = debounce((val) => {
        setSearchValue(val)
        if (!val?.length) {
            setResources(null);
        } else {
            fetchResources(val);
        }
    }, 300);

    const fetchDelegations = async () => {
        const res = await getDelegates();
        if (res.status) {
            setDelegatedActivities(delegatesDataMaker(res.entity));
        }
    };

    const handleCreateDelegate = async (data) => {
        const delegateDetail = {
            type: data?.access?.value,
            delegateId: data?.resource?.id,
            delegatedById: session?.data?.user?.id
        };

        const isDelegationExist = await checkDelegationIsExist(delegateDetail);

        if (isDelegationExist?.status && isDelegationExist?.entity?.length) {
            setToastData({ show: true, message: `The resource already has ${data?.access?.value} access.`, type: 'error' });
            onClose();
            return;
        }

        const delegateDetails = {
            type: data.access.value,
            delegateId: data.resource.id,
            subType: data.delegateAllIntakeRequests ? DELEGATION_SUB_TYPES.ALL_INTAKE_REQUEST : null,
            subActivityIds: []
        };

        const res = await createDelegate(delegateDetails);
        if (res.status) {
            fetchDelegations();
            setToastData({ show: true, message: 'Activity delegated successfully ' });
            onClose();
        } else {
            setToastData({ show: true, message: 'Failed to delegate activity', type: 'error' });
        }
    };

    const onDelegate = (data) => {
        handleCreateDelegate(data);
        setResources(null);
    };

    const handleRemoveDelegate = async ({ id, setShowDialog }) => {
        const res = await removeDelegate({ id, isOneRecord: true });
        if (res.status) {
            setDelegatedActivities((prev) => prev.filter((delegate) => delegate.delegation.id !== id));
            setToastData({ show: true, message: 'Delegation revoked successfully ' });
            setShowDialog('')
        } else {
            setTimeout(() => {
                setToastData({});
            }, 2000);
        }
    };

    const onRevokeAccess = (data, setShowDialog) => {
        handleRemoveDelegate({ id: data.delegation.id, setShowDialog });
    };

    const onSubmit = () => {
        onClose();
    };

    useEffect(() => {
        fetchDelegations();
    }, []);

    return (
        show ? <Delegate
            updateResources={updateResources}
            searchResults={resources}
            show={show}
            onClose={onClose}
            valueChanged={onSearch}
            delegatedActivities={delegatedActivities}
            onDelegate={onDelegate}
            onRevokeAccess={onRevokeAccess}
            onSubmit={onSubmit}
            setResources={setResources}
        /> : <></>
    );
}

export default DelegateActivity;
